import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, Copy, Globe, Link2, Loader2, Lock, MessageSquare, Trash2, UserPlus, Users, Video, X } from "@fg/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateShare, useRevokeShare, useShares } from "@/api/hooks";
import { driveApi, type DriveItem, type ShareLink } from "@/api/driveApi";
import { looksLikeEmail, sharingApi, type ShareRole } from "@/api/sharingApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

/** What the role menu offers — viewer with download blocked is Teams' "view only". */
type Access = "editor" | "viewer" | "viewer-nodl";

const ACCESS_LABEL: Record<Access, string> = {
    editor: "Editor",
    viewer: "Viewer",
    "viewer-nodl": "Viewer · no download"
};

const toAccess = (role: ShareRole, allowDownload: boolean): Access =>
    role === "editor" ? "editor" : allowDownload ? "viewer" : "viewer-nodl";

const fromAccess = (a: Access): { role: ShareRole; allowDownload: boolean } =>
    a === "editor" ? { role: "editor", allowDownload: true }
        : a === "viewer" ? { role: "viewer", allowDownload: true }
            : { role: "viewer", allowDownload: false };

/**
 * Google-Drive-style share dialog: add people as viewers or editors (viewers
 * optionally without download), see and change who has access — including
 * people a Connect chat or recording shared it with — and, below that,
 * share links for anyone / your organisation.
 */
export function ShareDialog({ item, onClose }: { item: DriveItem; onClose: () => void }) {
    const auth = useAuth();
    const token = useAccessToken();
    const qc = useQueryClient();
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const myDomain = useMemo(() => {
        const at = myEmail.indexOf("@");
        return at >= 0 ? myEmail.slice(at + 1) : "your organization";
    }, [myEmail]);

    const sharingKey = ["drive", "sharing", item.id, token] as const;
    const sharing = useQuery({
        queryKey: sharingKey,
        queryFn: () => sharingApi.get(item.id, token),
        enabled: Boolean(token)
    });
    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ["drive", "sharing", item.id] });
        void qc.invalidateQueries({ queryKey: ["drive", "shared-with-me"] });
    };

    // ---------- Add people ----------
    const [chips, setChips] = useState<string[]>([]);
    const [draft, setDraft] = useState("");
    const [newAccess, setNewAccess] = useState<Access>("viewer");
    const [error, setError] = useState<string | null>(null);
    const [sent, setSent] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const commitDraft = (): string[] => {
        const parts = draft.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean);
        if (parts.length === 0) return chips;
        const bad = parts.filter((p) => !looksLikeEmail(p));
        const next = [...chips, ...parts.filter((p) => looksLikeEmail(p) && !chips.includes(p))];
        setChips(next);
        setDraft(bad.join(" "));
        setError(bad.length ? `Not an email address: ${bad.join(", ")}` : null);
        return next;
    };

    const add = useMutation({
        mutationFn: (emails: string[]) => sharingApi.add(item.id, { emails, ...fromAccess(newAccess) }, token),
        onSuccess: (_data, emails) => {
            setChips([]);
            setDraft("");
            setSent(emails.length === 1 ? `Shared with ${emails[0]}` : `Shared with ${emails.length} people`);
            window.setTimeout(() => setSent(null), 3000);
            refresh();
        },
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't share.")
    });

    const submitPeople = () => {
        const emails = commitDraft();
        if (emails.length === 0) {
            setError("Add at least one email address.");
            return;
        }
        add.mutate(emails);
    };

    const update = useMutation({
        mutationFn: (v: { id: string; access: Access }) => sharingApi.update(v.id, fromAccess(v.access), token),
        onSuccess: refresh,
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't change access.")
    });
    const remove = useMutation({
        mutationFn: (id: string) => sharingApi.remove(id, token),
        onSuccess: refresh,
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't remove access.")
    });

    // Esc closes; the "Add people" box has focus on open.
    useEffect(() => {
        inputRef.current?.focus();
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const canManage = sharing.data?.canManage ?? false;
    const owner = sharing.data?.ownerEmail ?? "";
    const people = sharing.data?.people ?? [];

    // ---------- Links ----------
    const [copiedPeopleLink, setCopiedPeopleLink] = useState(false);
    const copyPeopleLink = async () => {
        // /open/<id> finds the item wherever it lives for whoever opens it.
        const url = driveApi.openLink(item.id);
        try {
            await navigator.clipboard.writeText(url);
            setCopiedPeopleLink(true);
            window.setTimeout(() => setCopiedPeopleLink(false), 2000);
        } catch { /* clipboard blocked */ }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Share ${item.name}`}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border bg-card shadow-2xl">
                <header className="flex items-center gap-2 border-b px-5 py-3">
                    <Users className="h-5 w-5 text-primary" />
                    <h2 className="min-w-0 flex-1 truncate text-base font-semibold" title={item.name}>Share “{item.name}”</h2>
                    <button type="button" onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent" aria-label="Close">
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
                    {/* Add people */}
                    {canManage || sharing.isPending ? (
                        <section className="space-y-2">
                            <div className="flex items-start gap-2">
                                <div
                                    className="flex min-h-9 flex-1 flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-1 focus-within:ring-2 focus-within:ring-primary"
                                    onClick={() => inputRef.current?.focus()}
                                >
                                    {chips.map((c) => (
                                        <span key={c} className="flex items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-xs text-primary">
                                            {c}
                                            <button
                                                type="button"
                                                onClick={() => setChips((cs) => cs.filter((x) => x !== c))}
                                                className="rounded-full p-0.5 hover:bg-primary/20"
                                                aria-label={`Remove ${c}`}
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        ref={inputRef}
                                        value={draft}
                                        onChange={(e) => { setDraft(e.target.value); setError(null); }}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                if (draft.trim()) commitDraft(); else if (chips.length) submitPeople();
                                            } else if (e.key === "," || e.key === ";" || e.key === " ") {
                                                if (draft.trim()) { e.preventDefault(); commitDraft(); }
                                            } else if (e.key === "Backspace" && !draft && chips.length) {
                                                setChips((cs) => cs.slice(0, -1));
                                            }
                                        }}
                                        onBlur={() => { if (draft.trim()) commitDraft(); }}
                                        placeholder={chips.length ? "" : "Add people by email"}
                                        aria-label="Add people by email"
                                        className="min-w-[8rem] flex-1 bg-transparent py-1 text-sm outline-none"
                                    />
                                </div>
                                <AccessMenu value={newAccess} onChange={setNewAccess} />
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-xs text-muted-foreground">
                                    {newAccess === "editor" ? "Editors can change, rename and share it."
                                        : newAccess === "viewer" ? "Viewers can open and download it."
                                            : "Viewers can open it but not download."}
                                </p>
                                <Button size="sm" onClick={submitPeople} disabled={add.isPending || (!chips.length && !draft.trim())} className="gap-1.5">
                                    {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Share
                                </Button>
                            </div>
                            {sent ? <p className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><Check className="h-3.5 w-3.5" /> {sent}</p> : null}
                        </section>
                    ) : null}

                    {error ? <p className="text-xs text-destructive" role="alert">{error}</p> : null}

                    {/* People with access */}
                    <section>
                        <h3 className="mb-2 text-sm font-semibold">People with access</h3>
                        {sharing.isPending ? (
                            <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</p>
                        ) : sharing.isError ? (
                            <p className="text-xs text-destructive">{sharing.error.message}</p>
                        ) : (
                            <ul className="space-y-1">
                                <PersonRow email={owner} you={owner.toLowerCase() === myEmail} label="Owner" />
                                {people.map((p) => (
                                    <PersonRow
                                        key={p.id}
                                        email={p.email}
                                        you={p.email === myEmail}
                                        source={p.source}
                                        label={canManage ? undefined : ACCESS_LABEL[toAccess(p.role, p.allowDownload)]}
                                        control={canManage ? (
                                            <AccessMenu
                                                value={toAccess(p.role, p.allowDownload)}
                                                onChange={(a) => update.mutate({ id: p.id, access: a })}
                                                onRemove={() => remove.mutate(p.id)}
                                                busy={(update.isPending && update.variables?.id === p.id) || (remove.isPending && remove.variables === p.id)}
                                            />
                                        ) : undefined}
                                    />
                                ))}
                            </ul>
                        )}
                        {people.length > 0 ? (
                            <button type="button" onClick={() => void copyPeopleLink()} className="mt-2 flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                                {copiedPeopleLink ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                {copiedPeopleLink ? "Copied" : "Copy link for people with access"}
                            </button>
                        ) : null}
                    </section>

                    {/* Links — only the owner manages those (the BFF scopes them to their creator). */}
                    {sharing.data?.myRole === "owner" ? <LinkSharing item={item} myDomain={myDomain} /> : null}
                </div>

                <footer className="flex justify-end border-t px-5 py-3">
                    <Button variant="outline" size="sm" onClick={onClose}>Done</Button>
                </footer>
            </div>
        </div>
    );
}

function PersonRow({
    email, you, label, source, control
}: {
    email: string;
    you: boolean;
    label?: string;
    source?: string | null;
    control?: React.ReactNode;
}) {
    return (
        <li className="flex items-center gap-3 rounded-md px-1 py-1.5">
            <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white", colourFor(email))}>
                {(email[0] ?? "?").toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{email}{you ? <span className="text-muted-foreground"> (you)</span> : null}</span>
                {source && source !== "drive" ? (
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        {source === "connect-recording" ? <Video className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                        {source === "connect-recording" ? "Meeting recording" : source === "connect-channel" ? "Shared in a Connect channel" : "Shared in a Connect chat"}
                    </span>
                ) : null}
            </span>
            {control ?? (label ? <span className="shrink-0 text-xs text-muted-foreground">{label}</span> : null)}
        </li>
    );
}

/** Small role picker: Editor / Viewer / Viewer · no download (+ Remove access). */
function AccessMenu({
    value, onChange, onRemove, busy
}: {
    value: Access;
    onChange: (a: Access) => void;
    onRemove?: () => void;
    busy?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    return (
        <div ref={ref} className="relative shrink-0">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); } }}
                aria-haspopup="menu"
                aria-expanded={open}
                className="flex h-9 items-center gap-1 rounded-md px-2 text-xs font-medium hover:bg-accent"
            >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {ACCESS_LABEL[value]}
                <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {open ? (
                <div role="menu" className="absolute right-0 top-10 z-10 w-52 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg">
                    {(Object.keys(ACCESS_LABEL) as Access[]).map((a) => (
                        <button
                            key={a}
                            type="button"
                            role="menuitemradio"
                            aria-checked={a === value}
                            onClick={() => { setOpen(false); if (a !== value) onChange(a); }}
                            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                        >
                            <span className="w-4">{a === value ? <Check className="h-3.5 w-3.5" /> : null}</span>
                            {ACCESS_LABEL[a]}
                        </button>
                    ))}
                    {onRemove ? (
                        <>
                            <div className="my-1 h-px bg-border" />
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => { setOpen(false); onRemove(); }}
                                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-destructive hover:bg-accent"
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Remove access
                            </button>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

type LinkMode = "anonymous" | "domain";

/** Share links: anyone with the link (optional password + expiry) or anyone in your organisation. */
function LinkSharing({ item, myDomain }: { item: DriveItem; myDomain: string }) {
    const shares = useShares(item.id);
    const create = useCreateShare();
    const revoke = useRevokeShare();
    const [mode, setMode] = useState<LinkMode>("domain");
    const [permission, setPermission] = useState<"view" | "edit">("view");
    const [password, setPassword] = useState("");
    const [expiry, setExpiry] = useState("");
    const [copied, setCopied] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const copy = async (link: ShareLink) => {
        try {
            await navigator.clipboard.writeText(driveApi.publicShareUrl(link.token));
            setCopied(link.id);
            window.setTimeout(() => setCopied(null), 2000);
        } catch { /* clipboard blocked */ }
    };

    const createLink = () => {
        setError(null);
        create.mutate(
            {
                id: item.id,
                permission,
                audience: mode,
                password: mode === "anonymous" && password.trim() ? password.trim() : undefined,
                // End of the chosen day in the viewer's time zone — "YYYY-MM-DD"
                // alone parses as UTC midnight, so links expired a day early.
                expiresAt: mode === "anonymous" && expiry ? new Date(`${expiry}T23:59:59`).toISOString() : undefined
            },
            {
                onSuccess: (link) => { void shares.refetch(); void copy(link); },
                onError: (e) => setError(e instanceof Error ? e.message : "Could not create the link.")
            }
        );
    };

    const links = shares.data ?? [];
    return (
        <section className="space-y-2 border-t pt-4">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold"><Link2 className="h-4 w-4" /> Links</h3>
            <div className="flex flex-wrap items-center gap-2">
                <select value={mode} onChange={(e) => setMode(e.target.value as LinkMode)} className="h-8 rounded-md border bg-background px-2 text-sm" aria-label="Who can open the link">
                    <option value="domain">People in {myDomain}</option>
                    <option value="anonymous">Anyone with the link</option>
                </select>
                <select value={permission} onChange={(e) => setPermission(e.target.value as "view" | "edit")} className="h-8 rounded-md border bg-background px-2 text-sm" aria-label="Link permission">
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                </select>
                <Button size="sm" variant="outline" onClick={createLink} disabled={create.isPending} className="h-8 gap-1.5">
                    {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Create & copy link
                </Button>
            </div>
            {mode === "anonymous" ? (
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-muted-foreground">
                        <span className="mb-1 flex items-center gap-1"><Lock className="h-3 w-3" /> Password (optional)</span>
                        <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-8" />
                    </label>
                    <label className="text-xs text-muted-foreground">
                        <span className="mb-1 block">Expires after (optional)</span>
                        <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="h-8" />
                    </label>
                </div>
            ) : null}
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
            {links.length > 0 ? (
                <ul className="space-y-1">
                    {links.map((s) => (
                        <li key={s.id} className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs">
                            {s.audience === "anonymous" ? <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                            <span className="min-w-0 flex-1 truncate">
                                {s.audience === "email" ? (s.allowedEmails ?? "Specific people")
                                    : s.audience === "domain" ? `People in ${myDomain}` : "Anyone with the link"}
                                <span className="text-muted-foreground">
                                    {" · "}{s.permission === "edit" ? "can edit" : "can view"}
                                    {s.hasPassword ? " · password" : ""}
                                    {s.expiresAt ? ` · until ${new Date(s.expiresAt).toLocaleDateString()}` : ""}
                                    {` · ${s.accessCount} open${s.accessCount === 1 ? "" : "s"}`}
                                </span>
                            </span>
                            <button type="button" onClick={() => void copy(s)} className="shrink-0 rounded p-1 hover:bg-accent" aria-label="Copy link">
                                {copied === s.id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                            <button type="button" onClick={() => revoke.mutate(s.id, { onSuccess: () => void shares.refetch() })} className="shrink-0 rounded p-1 text-destructive hover:bg-accent" aria-label="Delete link">
                                <Trash2 className="h-3.5 w-3.5" />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </section>
    );
}

const AVATAR_COLOURS = ["bg-sky-600", "bg-emerald-600", "bg-violet-600", "bg-amber-600", "bg-rose-600", "bg-teal-600", "bg-indigo-600"];

function colourFor(email: string): string {
    let h = 0;
    for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) | 0;
    return AVATAR_COLOURS[Math.abs(h) % AVATAR_COLOURS.length] ?? "bg-sky-600";
}
